import { TextField, FormControl, InputAdornment, IconButton, Box } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import { AutocompleteRenderInputParams } from "@material-ui/lab";
import * as React from "react";
import { forwardRef } from "react";
import { NumericFormat, NumericFormatProps } from "react-number-format";

import theme from "@/theme";

import { FormErrorText } from "./FormErrorText";
import CustomFormLabel from "./FormLabel";

const useStyles = makeStyles(() => ({
  formControl: {
    margin: theme.spacing(1, 0),
    minWidth: 161,
    width: "100%",
  },
  textField: {
    width: "100%",
  },
  adornment: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(2),
  },
  labelIcon: {
    marginLeft: theme.spacing(0.5),
  },
  inputRoot: {
    paddingRight: 0,
    height: 48,
  },
  input: {
    padding: "13.5px 16px",
  },
  helperText: {
    marginLeft: "14px",
    marginRight: "14px",
  },
  helperBelow: {
    "&&": {
      marginTop: theme.spacing(1),
      color: theme.palette.text.secondary,
      fontSize: 12,
      lineHeight: 1.5,
      whiteSpace: "pre-line",
    },
  },
  passwordToggle: {
    "&&": {
      width: 44,
      height: 44,
      padding: 12,
      color: theme.palette.semantic["icon/muted"],
    },
  },
  passwordIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 20,
    height: 20,
    "& svg": { width: 20, height: 20, fontSize: 20 },
    "& img": { display: "block", maxWidth: "100%", maxHeight: "100%" },
  },
  error: {
    borderColor: theme.palette.error.main,
    color: theme.palette.error.main,
  },
}));

interface NumericFormatCustomProps extends NumericFormatProps {
  inputRef: React.ForwardedRef<HTMLInputElement>;
  maxNumericLength?: number;
}

// カンマ区切り用のreact-number-formatのコンポーネント
function NumericFormatInput(props: NumericFormatCustomProps) {
  const { inputRef, maxNumericLength = 10, ...other } = props;

  return (
    <NumericFormat
      {...other}
      getInputRef={inputRef}
      allowLeadingZeros
      thousandSeparator=","
      inputMode="decimal"
      isAllowed={(values) => values.value.length <= maxNumericLength}
    />
  );
}

interface CustomFormProps {
  label?: string;
  labelLight?: string;
  value: string;
  onChange: (_event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (_event: React.FocusEvent<HTMLInputElement>) => void;
  helperText?: string;
  helperTextPlacement?: "label" | "below";
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  info?: string;
  type?: "text" | "password" | "month" | "number";
  unitSuffix?: string;
  autoComplete?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  errorText?: string | JSX.Element;
  placeHolder?: string;
  isShort?: boolean;
  formatNumeric?: boolean;
  maxNumericLength?: number;
  // 文字数の上限（formatNumeric では使わない。数値の桁数は maxNumericLength）
  maxLength?: number;
}

const CustomFormRef: React.ForwardRefRenderFunction<HTMLInputElement, CustomFormProps> = (
  {
    label,
    labelLight,
    value = "",
    onChange,
    onBlur,
    helperText = "",
    helperTextPlacement = "label",
    required = false,
    disabled = false,
    error = false,
    info = "",
    type = "text",
    unitSuffix = "",
    autoComplete = "off",
    inputMode,
    errorText = "",
    placeHolder = "",
    isShort,
    formatNumeric = false,
    maxNumericLength,
    maxLength,
  },
  ref
) => {
  const classes = useStyles();
  const [showPassword, setShowPassword] = React.useState(false);
  const handleClickShowPassword = () => setShowPassword(!showPassword);

  return (
    <FormControl className={classes.formControl} variant="outlined" error={error} disabled={disabled}>
      <Box display="flex" alignItems="center" mb={1}>
        {label && (
          <CustomFormLabel
            label={label}
            required={required}
            helperText={helperTextPlacement === "label" ? helperText : ""}
            info={info}
          />
        )}
        {labelLight && (
          <CustomFormLabel
            label={label}
            labelLight={labelLight}
            required={required}
            helperText={helperTextPlacement === "label" ? helperText : ""}
          />
        )}
      </Box>
      <TextField
        style={isShort ? { width: "161px" } : { width: "100%" }}
        variant="outlined"
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        type={type === "password" ? (showPassword ? "text" : "password") : type}
        InputProps={{
          ...(formatNumeric
            ? {
                inputComponent: NumericFormatInput,
                inputProps: { maxNumericLength, "aria-label": label },
              }
            : {}),
          classes: { root: classes.inputRoot, input: classes.input },
          endAdornment: (
            <>
              {type === "password" && (
                <InputAdornment position="end">
                  <IconButton
                    className={classes.passwordToggle}
                    onClick={handleClickShowPassword}
                    aria-label={showPassword ? "パスワードを非表示にする" : "パスワードを表示する"}
                    disabled={disabled}
                  >
                    <span className={classes.passwordIcon} aria-hidden="true">
                      {showPassword ? <VisibilityOff /> : <img src="/icons/ds-v2-eye.svg" alt="" />}
                    </span>
                  </IconButton>
                </InputAdornment>
              )}
              {unitSuffix && (
                <InputAdornment position="end" className={classes.adornment}>
                  {unitSuffix}
                </InputAdornment>
              )}
            </>
          ),
        }}
        placeholder={placeHolder}
        inputProps={{ inputMode, maxLength, "aria-label": label }}
        autoComplete={autoComplete}
        inputRef={ref}
        error={error}
        disabled={disabled}
        helperText={helperTextPlacement === "below" ? helperText : undefined}
        FormHelperTextProps={{ className: classes.helperBelow }}
      />
      {error && <FormErrorText>{errorText}</FormErrorText>}
    </FormControl>
  );
};

const CustomForm = forwardRef(CustomFormRef);
export { CustomForm };

interface CustomAutoCompleteFormProps {
  params?: AutocompleteRenderInputParams;
  label?: string;
  labelLight?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  info?: string;
  autoComplete?: string;
  errorText?: string | JSX.Element;
  placeHolder?: string;
  isShort?: boolean;
}

const CustomAutoCompleteFormRef: React.ForwardRefRenderFunction<HTMLInputElement, CustomAutoCompleteFormProps> = (
  {
    params,
    label,
    labelLight,
    helperText = "",
    required = false,
    disabled = false,
    error = false,
    info = "",
    errorText = "",
    placeHolder = "",
    isShort,
  },
  ref
) => {
  const classes = useStyles();

  return (
    <FormControl className={classes.formControl} variant="outlined" error={error} disabled={disabled}>
      <Box display="flex" alignItems="center" mb={1}>
        {label && <CustomFormLabel label={label} required={required} helperText={helperText} info={info} />}
        {labelLight && (
          <CustomFormLabel label={label} labelLight={labelLight} required={required} helperText={helperText} />
        )}
      </Box>
      <TextField
        {...params}
        style={isShort ? { width: "161px" } : { width: "100%" }}
        variant="outlined"
        InputProps={{
          classes: { root: classes.inputRoot, input: classes.input },
          ...params.InputProps,
        }}
        ref={ref}
        placeholder={placeHolder}
        error={error}
        disabled={disabled}
      />
      {error && <FormErrorText>{errorText}</FormErrorText>}
    </FormControl>
  );
};

const CustomAutoCompleteForm = forwardRef(CustomAutoCompleteFormRef);
export { CustomAutoCompleteForm };

// サンプルコンポーネントでカスタムフォームを使用する
const FormExample: React.FC = () => {
  const [value, setValue] = React.useState("");

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
  };

  return (
    <div>
      <CustomForm label="ラベル" value={value} onChange={handleChange} required placeHolder="テキスト" />
      <CustomForm
        label="ラベル"
        value={value}
        onChange={handleChange}
        helperText="テキストが入ります"
        required
        placeHolder="テキスト"
      />
      <CustomForm
        label="ラベル"
        value={value}
        onChange={handleChange}
        error
        errorText="エラーテキストが入ります"
        required
        placeHolder="テキスト"
      />
      <CustomForm
        label="ラベル"
        value={value}
        onChange={handleChange}
        type="password"
        required
        placeHolder="テキスト"
      />
      <CustomForm
        label="ラベル"
        value={value}
        onChange={handleChange}
        unitSuffix="年"
        required
        info="追加情報"
        placeHolder="テキスト"
      />
    </div>
  );
};

export default FormExample;
